import React, { useState } from 'react';
import { useValueChange } from '@leverege/ui-hooks';
import { ModelFactory, ModelListEditor, ItemLayoutSelector } from '@leverege/ui-plugin';
import { Pane, IdGen, Label, TextInput, Text, Flex, TabPane, Checkbox } from '@leverege/ui-elements';
import { ns } from '@leverege/i18n';
import Model from './DetailListModel.js';
import Details from '../Details.js';
import DetailListRenderer from "./DetailListRenderer.js";
import S from './DetailListEditor.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const {
  t
} = ns('ui-attributes.detail.DetailListEditor');
function contentHeader(item) {
  if (item == null) {
    return null;
  }
  const model = ModelFactory.get(item.type);
  let name;
  if (model) {
    name = model?.getName(item);
  }
  if (name == null) {
    name = item.displayName || item.name || item.type || t('unknown');
  }
  return /*#__PURE__*/_jsx(Text, {
    variant: "subtitle",
    children: name
  });
}
function DetailListEditor(props) {
  const {
    value,
    onChange,
    eventData,
    objectType
  } = props;
  if (value == null) {
    return null;
  }
  const [ids] = useState(IdGen.createIds('dle', 12));
  let idn = 0;
  const onModelChange = useValueChange(Model, props);
  const onAddDetail = evt => {
    const nModel = Model.details().add(value, evt.value.create());
    onChange({
      data: eventData,
      value: nModel,
      oldValue: value
    });
  };
  const header = item => {
    const avail = Details.getDetailsFor(objectType);
    return /*#__PURE__*/_jsx(Flex, {
      align: "center",
      justify: "space-between",
      children: /*#__PURE__*/_jsx(ItemLayoutSelector, {
        showFilter: true,
        options: avail,
        onSelect: onAddDetail,
        icon: "fa fa-plus",
        title: t('add')
      })
    });
  };
  const forward = e => {
    onChange({
      value: e.value,
      data: eventData
    });
  };
  return /*#__PURE__*/_jsxs(TabPane, {
    style: {
      position: 'relative',
      height: '100%'
    },
    children: [/*#__PURE__*/_jsx(TabPane.Tab, {
      tabName: t('attributes'),
      tabId: "attrs",
      children: /*#__PURE__*/_jsx(ModelListEditor, {
        value: value,
        listClassName: S.list,
        listHeader: header,
        selectionHeader: contentHeader,
        model: Model.details(),
        renderer: DetailListRenderer,
        onChange: forward,
        selectedProps: {
          objectType,
          ...props
        }
      })
    }), /*#__PURE__*/_jsx(TabPane.Tab, {
      tabName: t('options'),
      tabId: "options",
      children: /*#__PURE__*/_jsxs(Pane, {
        variant: "editor|padding",
        className: S.grid,
        children: [/*#__PURE__*/_jsx(Label, {
          variant: "input",
          htmlFor: ids[idn],
          children: t('variant')
        }), /*#__PURE__*/_jsx(TextInput, {
          id: ids[idn++],
          eventData: "setVariant",
          hint: t('variantHint'),
          value: Model.getVariant(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Label, {
          variant: "input",
          htmlFor: ids[idn],
          children: t('background')
        }), /*#__PURE__*/_jsx(TextInput, {
          id: ids[idn++],
          eventData: "setBackground",
          hint: t('backgroundHint'),
          value: Model.getBackground(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Label, {
          variant: "input",
          htmlFor: ids[idn],
          children: t('evenBackground')
        }), /*#__PURE__*/_jsx(TextInput, {
          id: ids[idn++],
          eventData: "setEvenRowBackground",
          hint: t('evenBackgroundHint'),
          value: Model.getEvenRowBackground(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Label, {
          variant: "input",
          htmlFor: ids[idn],
          children: t('oddBackground')
        }), /*#__PURE__*/_jsx(TextInput, {
          id: ids[idn++],
          eventData: "setOddRowBackground",
          hint: t('oddBackgroundHint'),
          value: Model.getOddRowBackground(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Label, {
          variant: "input",
          htmlFor: ids[idn],
          children: t('titleColor')
        }), /*#__PURE__*/_jsx(TextInput, {
          id: ids[idn++],
          eventData: "setHeaderNameColor",
          hint: t('titleColorhint'),
          value: Model.getHeaderNameColor(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Label, {
          variant: "input",
          htmlFor: ids[idn],
          children: t('labelColor')
        }), /*#__PURE__*/_jsx(TextInput, {
          id: ids[idn++],
          eventData: "setLabelColor",
          hint: t('labelColor'),
          value: Model.getLabelColor(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Label, {
          variant: "input",
          htmlFor: ids[idn],
          children: t('valueColor')
        }), /*#__PURE__*/_jsx(TextInput, {
          id: ids[idn++],
          eventData: "setValueColor",
          hint: t('valueColorHint'),
          value: Model.getValueColor(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Label, {
          variant: "input",
          htmlFor: ids[idn],
          children: t('suffixColor')
        }), /*#__PURE__*/_jsx(TextInput, {
          id: ids[idn++],
          eventData: "setSuffixColor",
          hint: t('suffixColorHint'),
          value: Model.getSuffixColor(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Label, {
          variant: "input",
          htmlFor: ids[idn],
          children: t('headerVisible')
        }), /*#__PURE__*/_jsx(Checkbox, {
          id: ids[idn++],
          eventData: "setHeaderVisible",
          value: Model.getHeaderVisible(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Label, {
          variant: "input",
          htmlFor: ids[idn],
          children: t('headerControlsVisible')
        }), /*#__PURE__*/_jsx(Checkbox, {
          id: ids[idn++],
          eventData: "setHeaderControlsVisible",
          value: Model.getHeaderControlsVisible(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Label, {
          variant: "input",
          htmlFor: ids[idn],
          children: t('footerVisible')
        }), /*#__PURE__*/_jsx(Checkbox, {
          id: ids[idn++],
          eventData: "setFooterVisible",
          value: Model.getFooterVisible(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Label, {
          variant: "input",
          htmlFor: ids[idn],
          children: t('titleAttribute')
        }), /*#__PURE__*/_jsx(TextInput, {
          id: ids[idn++],
          eventData: "setHeaderNameAttribute",
          hint: t('titleAttributeHint'),
          value: Model.getHeaderNameAttribute(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Label, {
          variant: "input",
          htmlFor: ids[idn],
          children: t('iconAttribute')
        }), /*#__PURE__*/_jsx(TextInput, {
          id: ids[idn++],
          eventData: "setHeaderIconAttribute",
          hint: t('iconAttributeHint'),
          value: Model.getHeaderIconAttribute(value),
          onChange: onModelChange
        }), /*#__PURE__*/_jsx(Label, {
          variant: "input",
          htmlFor: ids[idn],
          children: t('iconSize')
        }), /*#__PURE__*/_jsx(TextInput, {
          id: ids[idn++],
          eventData: "setHeaderIconSize",
          hint: t('iconSizeHint'),
          value: Model.getHeaderIconSize(value),
          onChange: onModelChange
        })]
      })
    })]
  });
}
export default DetailListEditor;